import {
  accountActions,
  accountSelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/account';
import { authSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/auth';
import { compose } from 'recompose';
import { connect, useSelector } from 'react-redux';
import {
  evaluatedPolicyActions,
  evaluatedPolicySelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/evaluatedPolicy';
import {
  featuresActions,
  featuresSelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import { LayoutManager } from '@lifeomic/chroma-react/components/LayoutManager';
import { KeymapHelp } from '@lifeomic/chroma-react/components/KeymapHelp';
import {
  projectPickerActions,
  projectPickerSelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { push } from 'connected-react-router';
import { withRouter } from 'react-router-dom';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { AccountSwitcher } from './AccountSwitcher';
import Header from './header/Header';
import PropTypes from 'prop-types';
import React from 'react';
import urls from '../../util/urls';
import { PMP } from './PMP';
import { KEY_MAP_DOCS } from '../../util/keyboardShortcuts';
import SidebarNav from './SidebarNav';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { useProcessMarketplaceQueryParams } from './useProcessMarketplaceQueryParams';

const useStyles = makeStyles(() => ({
  content: {
    width: '100%',
    height: '100%',
    flex: 1,
    display: 'flex',
  },
}));

const AppContainer = ({
  accounts,
  activeAccount,
  areFeaturesFetching,
  children,
  fetchEvaluatedPolicy,
  fetchFeatures,
  isAccountFetching,
  isAuthFetching,
  isPolicyFetching,
  isProjectFetching,
  push,
  setActiveAccount,
  setActiveProject,
  forceShowHeader = false,
  forceShowSidebarNav = false,
}) => {
  const [isAccountDialogOpen, setIsAccountDialogOpen] = React.useState(false);
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const classes = useStyles({});

  useProcessMarketplaceQueryParams();

  const handleAccountClick = () => {
    setIsAccountDialogOpen(!isAccountDialogOpen);

    if (accounts.length === 1) {
      push(urls.app.roots.account());
    }
  };

  const handleAccountChange = async account => {
    // The awaits here work around an issue where other parts of the UI
    // will query for projects in the wrong account when the account is
    // in the middle switching.
    await setActiveProject(null);
    await setActiveAccount(account.id);
    fetchEvaluatedPolicy();
    fetchFeatures();
    setIsAccountDialogOpen(false);
  };

  const handleCreateAccountClick = () => {
    setIsAccountDialogOpen(false);
  };

  const handleManageAccountClick = async account => {
    if ((activeAccount && activeAccount.id) === (account && account.id)) {
      setIsAccountDialogOpen(false);
      push(urls.app.roots.account());
      return;
    }

    await handleAccountChange(account);
    push(urls.app.roots.account());
  };

  const handleEscapeKeyDown = () => {
    setIsAccountDialogOpen(false);
  };

  const isChangingAccounts =
    isAuthFetching ||
    areFeaturesFetching ||
    isAccountFetching ||
    isPolicyFetching ||
    isProjectFetching;

  return (
    <React.Fragment>
      <PMP />
      <KeymapHelp keyMapDocs={KEY_MAP_DOCS} />
      <LayoutManager
        header={
          forceShowHeader || isAuthenticated ? (
            <Header onAccountClick={handleAccountClick} />
          ) : null
        }
        sidebar={forceShowSidebarNav || isAuthenticated ? <SidebarNav /> : null}
      >
        <div
          className={classes.content}
          {...getTestProps('appContainer-mainContentContainer')}
        >
          {children}
        </div>
      </LayoutManager>
      {isAccountDialogOpen && (
        <AccountSwitcher
          onCancelClick={handleAccountClick}
          onAccountChange={handleAccountChange}
          onCreateAcountClick={handleCreateAccountClick}
          onManageClick={handleManageAccountClick}
          isOpen={isAccountDialogOpen}
          isLoading={isChangingAccounts}
          onEscapeKeyDown={handleEscapeKeyDown}
        />
      )}
    </React.Fragment>
  );
};

AppContainer.propTypes = {
  accounts: PropTypes.array,
  activeAccount: PropTypes.object,
  areFeaturesFetching: PropTypes.bool,
  children: PropTypes.any,
  classes: PropTypes.object,
  fetchEvaluatedPolicy: PropTypes.func,
  fetchFeatures: PropTypes.func,
  isAccountFetching: PropTypes.bool,
  isAuthFetching: PropTypes.bool,
  isPolicyFetching: PropTypes.bool,
  isProjectFetching: PropTypes.bool,
  push: PropTypes.func,
  refreshAccessToken: PropTypes.func,
  setActiveAccount: PropTypes.func,
  setActiveProject: PropTypes.func,
  showNav: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    accounts: accountSelectors.selectAccounts(state),
    activeAccount: accountSelectors.selectActiveAccount(state),
    areFeaturesFetching: featuresSelectors.selectIsFetching(state),
    isAccountFetching: accountSelectors.selectIsFetching(state),
    isAuthFetching: authSelectors.selectOauth(state).isFetching,
    isPolicyFetching:
      evaluatedPolicySelectors.selectPolicyIsFetchingForUser(state),
    isProjectFetching: projectPickerSelectors.selectIsFetching(state),
  };
};

const mapDispatchToProps = {
  fetchEvaluatedPolicy: evaluatedPolicyActions.fetchEvaluatedPolicy,
  fetchFeatures: featuresActions.fetchFeatures,
  push,
  setActiveAccount: accountActions.setActiveAccount,
  setActiveProject: projectPickerActions.setActiveProject,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(AppContainer);
